<template>
  <div class="clock-entries-stats">
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
      <v-progress-circular indeterminate :color="$getPrimaryButton('color')" size="56"></v-progress-circular>
    </div>

    <div v-else-if="!statsData" class="d-flex flex-column align-center justify-center" style="min-height: 300px;">
      <v-icon size="72" color="grey lighten-2">mdi-calendar-search</v-icon>
      <p class="text--secondary mt-3">Selecciona un periodo para ver las estadísticas</p>
    </div>

    <div v-else>
      <!-- Tarjetas resumen -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <v-icon size="36" :color="$getPrimaryButton('color')">mdi-account-group</v-icon>
              <div class="text-h4 font-weight-bold mt-2">{{ statsData.totalWorkers }}</div>
              <div class="text-caption text--secondary">Trabajadores</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <v-icon size="36" :color="$getPrimaryButton('color')">mdi-clock-outline</v-icon>
              <div class="text-h4 font-weight-bold mt-2">{{ statsData.totalEntries }}</div>
              <div class="text-caption text--secondary">Fichajes totales</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <v-icon size="36" color="warning">mdi-alert-outline</v-icon>
              <div class="text-h4 font-weight-bold mt-2">{{ statsData.totalAlerts }}</div>
              <div class="text-caption text--secondary">Alertas</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card" elevation="1">
            <v-card-text class="text-center">
              <v-icon size="36" color="success">mdi-calendar-check</v-icon>
              <div class="text-h4 font-weight-bold mt-2">{{ statsData.totalDays }}</div>
              <div class="text-caption text--secondary">Días con actividad</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tabla resumen por trabajador -->
      <v-card elevation="1">
        <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
          <v-icon :color="$getColor('vuetifyColor1')" class="mr-2">mdi-account-details</v-icon>
          Resumen por trabajador
        </v-card-title>
        <v-divider></v-divider>
        <v-simple-table>
          <thead>
            <tr>
              <th>Trabajador</th>
              <th>Días</th>
              <th>Fichajes</th>
              <th>Horas est.</th>
              <th>Alertas</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="w in statsData.workers" :key="w.workerId">
              <td>
                <div class="d-flex align-center">
                  <v-avatar :color="$getColor('vuetifyColor1')" size="28" class="mr-2">
                    <span class="white--text text-caption">{{ initials(w.workerName, w.workerLastName) }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ w.workerName }} {{ w.workerLastName }}</div>
                    <div class="text-caption text--secondary">{{ w.workerEmail }}</div>
                  </div>
                </div>
              </td>
              <td>{{ w.days }}</td>
              <td>{{ w.entries }}</td>
              <td>{{ w.estimatedHours }}h</td>
              <td>
                <v-chip x-small :color="w.alerts > 0 ? 'warning' : 'success'" dark>
                  {{ w.alerts }}
                </v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import clockEntryService from '@/services/clockentry.service';

export default {
  name: 'clock-entries-stats',
  props: {
    dateFrom: { type: String, default: null },
    dateTo: { type: String, default: null },
    workcenterId: { type: [String, Number], default: null },
  },
  data() {
    return {
      loading: false,
      statsData: null,
    };
  },
  mounted() {
    if (this.dateFrom && this.dateTo && this.workcenterId) {
      this.loadStats();
    }
  },
  watch: {
    dateFrom() { this.loadStats(); },
    dateTo() { this.loadStats(); },
  },
  methods: {
    async loadStats() {
      if (!this.dateFrom || !this.dateTo || !this.workcenterId) return;
      this.loading = true;
      this.statsData = null;
      try {
        const response = await clockEntryService.getWorkersClockEntriesInRange(this.workcenterId, this.dateFrom, this.dateTo);
        const mapped = response.map((item) => ({
          ...item,
          time: clockEntryService.unixTimeToDateTime(item.unixClockInTime),
          timeLocale: clockEntryService.unixTimeToLocaleDateTime(item.unixClockInTime),
          sourceType: item.type,
          workerName: item.worker.name,
          workerLastName: item.worker.lastName1,
          workerEmail: item.worker.email,
          workerId: item.worker.id,
          worker: item.worker,
        }));

        const grouped = clockEntryService.getGroupedEntriesForAllWorkers(mapped);
        const workers = Object.values(grouped).map((wd) => {
          const alerts = wd.groupedEntries.filter((d) => this.dayHasAlerts(d)).length;
          const hours = wd.groupedEntries.reduce((acc, d) => acc + (d.workTime || 0), 0);
          return {
            workerId: wd.worker.id,
            workerName: wd.worker.name,
            workerLastName: wd.worker.lastName1,
            workerEmail: wd.worker.email,
            days: wd.groupedEntries.length,
            entries: wd.groupedEntries.reduce((a, d) => a + (d.entries?.length || 0), 0),
            estimatedHours: hours.toFixed(1),
            alerts,
          };
        });

        this.statsData = {
          totalWorkers: workers.length,
          totalEntries: mapped.length,
          totalAlerts: workers.reduce((a, w) => a + w.alerts, 0),
          totalDays: workers.reduce((a, w) => a + w.days, 0),
          workers,
        };
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    dayHasAlerts(day) {
      return day.alertDayStartWithClockOut || day.alertDayEndWithClockIn
        || day.alertDayWithManualEntries || day.alertDayWithAdminEntries
        || day.alertDayWithUnequalEntries || day.alertDayWithConsecutivesSameTypes;
    },
    initials(name, lastName) {
      return `${(name || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
    },
  },
};
</script>

<style scoped lang="scss">
.clock-entries-stats {
  width: 100%;
}
.stat-card {
  border-radius: 12px;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.12) !important; }
}
</style>
